/**
 * Policy Detail Page
 *
 * Create or edit a policy with permissions configuration
 * 
 * IMPORTANT: Permission changes are staged locally and only saved when the main Save button is clicked.
 * This matches the reference implementation which batches permission changes into:
 * { permissions: { create: [], update: [], delete: [] } }
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    ArrowLeft,
    Save,
    Loader2,
    Plus,
    X,
    Check,
    ChevronDown,
    Ban,
    CheckCircle,
    Settings,
} from 'lucide-react';
import {
    usePolicy,
    usePolicyPermissions,
} from '@/hooks/useAccessControl';
import { useCollections, isSystemCollection } from '@/hooks/useCollections';
import type { Permission } from '@/stores/access-control';
import { PermissionDetailSheet } from '@/components/permissions';
import { api, ApiResponse } from '@/lib/api';
import { toast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

// Type alias for permission items
type PermissionItem = Permission;

// Permission access levels
type AccessLevel = 'none' | 'full' | 'custom';

interface PolicyFormData {
    name: string;
    icon: string;
    description: string;
    ip_access: string;
    enforce_tfa: boolean;
    admin_access: boolean;
    app_access: boolean;
}

const ACTIONS = ['create', 'read', 'update', 'delete', 'share'] as const;
type ActionType = typeof ACTIONS[number];

// System collections available for permissions
const SYSTEM_COLLECTIONS = [
    'cms_activity',
    'cms_collections',
    'cms_comments',
    'cms_dashboards',
    'cms_extensions',
    'cms_fields',
    'cms_files',
    'cms_flows',
    'cms_folders',
    'cms_migrations',
    'cms_notifications',
    'cms_operations',
    'cms_panels',
    'cms_permissions',
    'cms_policies',
    'cms_presets',
    'cms_relations',
    'cms_revisions',
    'cms_roles',
    'cms_sessions',
    'cms_settings',
    'cms_shares',
    'cms_translations',
    'cms_users',
    'cms_versions',
    'cms_webhooks',
];

// App Access Minimal Permissions - Required when app_access is enabled
// These are automatically applied and cannot be removed
interface AppAccessPermission {
    collection: string;
    actions: readonly string[];
    required: boolean; // If true, cannot be removed
}

const APP_ACCESS_MINIMAL_PERMISSIONS: AppAccessPermission[] = [
    { collection: 'cms_activity', actions: ['read'], required: true },
    { collection: 'cms_collections', actions: ['read'], required: true },
    { collection: 'cms_comments', actions: ['create', 'read', 'update', 'delete'], required: true },
    { collection: 'cms_fields', actions: ['read'], required: true },
    { collection: 'cms_notifications', actions: ['read', 'update'], required: false },
    { collection: 'cms_presets', actions: ['create', 'read', 'update', 'delete'], required: false },
    { collection: 'cms_relations', actions: ['read'], required: true },
    { collection: 'cms_roles', actions: ['read'], required: false },
    { collection: 'cms_settings', actions: ['read'], required: true },
    { collection: 'cms_shares', actions: ['read'], required: false },
    { collection: 'cms_translations', actions: ['read'], required: true },
    { collection: 'cms_users', actions: ['read'], required: true },
];

// Action Button Component with dropdown
interface ActionButtonProps {
    collection: string;
    action: ActionType;
    accessLevel: AccessLevel;
    isAppMinimal: boolean;  // If true, this is a required app permission - hide No Access option
    onChangeAccess: (level: AccessLevel) => void;
    onCustomAccess: () => void;
}

function ActionButton({ action, accessLevel, isAppMinimal, onChangeAccess, onCustomAccess }: ActionButtonProps) {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getButtonStyle = () => {
        switch (accessLevel) {
            case 'full':
                return 'bg-primary text-primary-foreground hover:bg-primary/90';
            case 'custom':
                return 'bg-orange-500 text-white hover:bg-orange-600';
            default:
                return 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent border border-border';
        }
    };

    const handleSelect = (level: AccessLevel) => {
        if (level === 'custom') {
            // Open the permission detail sheet
            onCustomAccess();
            setShowDropdown(false);
            return;
        }
        onChangeAccess(level);
        setShowDropdown(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowDropdown(!showDropdown)}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${getButtonStyle()}`}
                title={`${action} - Click to change access level`}
            >
                {action.charAt(0).toUpperCase() + action.slice(1)}
            </Button>

            {/* Dropdown Menu */}
            {showDropdown && (
                <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-card py-1 shadow-lg">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleSelect('full')}
                        className={`flex w-full items-center justify-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${accessLevel === 'full' ? 'bg-accent' : ''
                            }`}
                    >
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <div>
                            <div className="font-medium text-foreground">All Access</div>
                        </div>
                        {accessLevel === 'full' && <Check className="ml-auto h-4 w-4 text-primary" />}
                    </Button>

                    {/* Only show No Access if not an app minimal permission */}
                    {!isAppMinimal && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => handleSelect('none')}
                            className={`flex w-full items-center justify-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${accessLevel === 'none' ? 'bg-accent' : ''
                                }`}
                        >
                            <Ban className="h-4 w-4 text-gray-500" />
                            <div>
                                <div className="font-medium text-foreground">No Access</div>
                            </div>
                            {accessLevel === 'none' && <Check className="ml-auto h-4 w-4 text-primary" />}
                        </Button>
                    )}

                    <div className="my-1 border-t border-border" />

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleSelect('custom')}
                        className="flex w-full items-center justify-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    >
                        <Settings className="h-4 w-4 text-orange-500" />
                        <div>
                            <div className="font-medium text-foreground">Use Custom</div>
                        </div>
                        <ChevronDown className="ml-auto h-4 w-4 rotate-[-90deg] text-muted-foreground" />
                    </Button>
                </div>
            )}
        </div>
    );
}

export function PolicyDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isNew = id === 'new';

    // Data fetching
    const { data: policy, isLoading: policyLoading, refetch: refetchPolicy } = usePolicy(isNew ? undefined : id);
    const { data: fetchedPermissions, refetch: refetchPermissions } = usePolicyPermissions(isNew ? undefined : id);
    const { data: allCollections } = useCollections();

    // Form state
    const [formData, setFormData] = useState<PolicyFormData>({
        name: '',
        icon: 'badge',
        description: '',
        ip_access: '',
        enforce_tfa: false,
        admin_access: false,
        app_access: true,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [newPermission, setNewPermission] = useState({
        collection: '',
        actions: [] as string[],
    });

    // Permission changes tracking (staged locally, saved on main Save button)
    // Format matches reference: { create: [], update: [], delete: [] }
    interface PermissionChanges {
        create: Array<Omit<Permission, 'id'>>;
        update: Array<Permission>;
        delete: string[]; // IDs of permissions to delete
    }
    const [permissionChanges, setPermissionChanges] = useState<PermissionChanges>({
        create: [],
        update: [],
        delete: [],
    });

    // Compute effective permissions (fetched + local changes)
    const permissions = useCallback((): PermissionItem[] => {
        if (!fetchedPermissions) return [];

        // Start with fetched permissions
        let result = [...fetchedPermissions] as PermissionItem[];

        // Remove deleted ones (compare as strings since delete array contains strings)
        result = result.filter(p => !permissionChanges.delete.includes(String(p.id)));

        // Apply updates (compare as strings)
        result = result.map(p => {
            const update = permissionChanges.update.find(u => String(u.id) === String(p.id));
            return update ? { ...p, ...update } : p;
        });

        // Add created ones (with temporary IDs)
        const createdWithTempIds = permissionChanges.create.map((p, idx) => ({
            ...p,
            id: `temp-${idx}`,
        })) as PermissionItem[];

        result = [...result, ...createdWithTempIds];

        return result;
    }, [fetchedPermissions, permissionChanges])();

    // Permission detail sheet state
    const [activePermission, setActivePermission] = useState<{
        permissionId: string | null;
        collection: string;
        action: ActionType;
        // Store full permission data for the sheet
        permissionData?: Partial<Permission>;
    } | null>(null);

    // Load existing policy data
    useEffect(() => {
        if (policy) {
            setFormData({
                name: policy.name,
                icon: policy.icon,
                description: policy.description || '',
                ip_access: policy.ip_access || '',
                enforce_tfa: policy.enforce_tfa,
                admin_access: policy.admin_access,
                app_access: policy.app_access,
            });
        }
    }, [policy]);

    // Group permissions by collection
    const permissionsByCollection = permissions?.reduce<Record<string, PermissionItem[]>>(
        (acc, perm) => {
            if (!acc[perm.collection]) {
                acc[perm.collection] = [];
            }
            acc[perm.collection]!.push(perm);
            return acc;
        },
        {}
    );

    // Get collections already configured with permissions (to exclude from "Add Collection" dropdown)
    const configuredCollections = useMemo(() => {
        const configured = new Set<string>();

        // Add collections from existing permissions
        if (permissionsByCollection) {
            Object.keys(permissionsByCollection).forEach(c => configured.add(c));
        }

        // Add collections from staged creates
        permissionChanges.create.forEach(p => configured.add(p.collection));

        // Add app minimal collections if app_access is enabled
        if (formData.app_access) {
            APP_ACCESS_MINIMAL_PERMISSIONS.forEach(ap => configured.add(ap.collection));
        }

        return configured;
    }, [permissionsByCollection, permissionChanges.create, formData.app_access]);

    // Available collections for "Add Collection" dropdown
    // Split into user collections and system collections, like reference implementation
    const availableCollections = useMemo(() => {
        // User collections (from API, excluding system collections)
        const userCollections = (allCollections || [])
            .filter(c => !isSystemCollection(c.collection))
            .filter(c => !configuredCollections.has(c.collection))
            .sort((a, b) => a.collection.localeCompare(b.collection));

        // System collections (from hardcoded list)
        const systemCollections = SYSTEM_COLLECTIONS
            .filter(c => !configuredCollections.has(c))
            .sort((a, b) => a.localeCompare(b));

        return { userCollections, systemCollections };
    }, [allCollections, configuredCollections]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Check if there are any unsaved changes
    const hasPermissionChanges =
        permissionChanges.create.length > 0 ||
        permissionChanges.update.length > 0 ||
        permissionChanges.delete.length > 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            if (isNew) {
                // Create new policy
                const response = await api.post<ApiResponse<{ id: string }>>('/policies', {
                    name: formData.name,
                    icon: formData.icon,
                    description: formData.description || null,
                    ip_access: formData.ip_access || null,
                    enforce_tfa: formData.enforce_tfa,
                    admin_access: formData.admin_access,
                    app_access: formData.app_access,
                });
                const newPolicy = response.data;
                if (newPolicy?.id) {
                    toast.success('Policy created successfully');
                    navigate(`/admin/settings/policies/${newPolicy.id}`, { replace: true });
                }
            } else if (id) {
                // Update existing policy with permission changes
                // Format: { permissions: { create: [], update: [], delete: [] }, ...policyFields }
                const updateData: Record<string, unknown> = {
                    name: formData.name,
                    icon: formData.icon,
                    description: formData.description || null,
                    ip_access: formData.ip_access || null,
                    enforce_tfa: formData.enforce_tfa,
                    admin_access: formData.admin_access,
                    app_access: formData.app_access,
                };

                // Only include permissions if there are changes
                if (hasPermissionChanges) {
                    updateData.permissions = {
                        create: permissionChanges.create,
                        update: permissionChanges.update,
                        delete: permissionChanges.delete,
                    };
                }

                await api.patch(`/policies/${id}`, updateData);

                // Reset permission changes after successful save
                setPermissionChanges({ create: [], update: [], delete: [] });

                // Refetch data to get fresh state
                await refetchPolicy();
                await refetchPermissions();

                toast.success('Policy saved successfully');
            }
        } catch (err) {
            console.error('Failed to save policy:', err);
            toast.error('Failed to save policy');
        } finally {
            setIsSaving(false);
        }
    };

    // Stage adding new permissions (for the Add Collection modal)
    const handleAddPermissions = () => {
        if (!id || isNew || !newPermission.collection || newPermission.actions.length === 0)
            return;

        const newCreates = newPermission.actions.map(action => ({
            policy: id,
            collection: newPermission.collection,
            action: action as 'create' | 'read' | 'update' | 'delete' | 'share',
            fields: ['*'],
            permissions: null,
            validation: null,
            presets: null,
        }));

        setPermissionChanges(prev => ({
            ...prev,
            create: [...prev.create, ...newCreates],
        }));

        setShowPermissionModal(false);
        setNewPermission({ collection: '', actions: [] });
    };

    // Stage deleting a permission
    const handleDeletePermission = (permissionId: string | number) => {
        const idStr = String(permissionId);
        // Check if it's a temporary ID (staged create)
        if (idStr.startsWith('temp-')) {
            const idx = parseInt(idStr.replace('temp-', ''));
            setPermissionChanges(prev => ({
                ...prev,
                create: prev.create.filter((_, i) => i !== idx),
            }));
        } else {
            // Check if it's in the update list, remove from there first
            setPermissionChanges(prev => ({
                ...prev,
                update: prev.update.filter(p => String(p.id) !== idStr),
                delete: [...prev.delete, idStr],
            }));
        }
    };

    // Handle changing access level for a collection/action (stages changes locally)
    const handleAccessChange = (
        collection: string,
        action: ActionType,
        level: AccessLevel,
        existingPermission?: PermissionItem
    ) => {
        if (!id || isNew) return;

        if (level === 'none') {
            if (existingPermission) {
                // Stage deletion
                handleDeletePermission(existingPermission.id);
            }
            // If no existingPermission, nothing to delete - already "none"
        } else if (level === 'full') {
            if (existingPermission) {
                // Check if it's a temporary permission
                const idStr = String(existingPermission.id);
                if (idStr.startsWith('temp-')) {
                    // Update the staged create
                    const idx = parseInt(idStr.replace('temp-', ''));
                    setPermissionChanges(prev => {
                        const newCreate = [...prev.create];
                        if (newCreate[idx]) {
                            newCreate[idx] = {
                                ...newCreate[idx],
                                collection: newCreate[idx].collection!,
                                action: newCreate[idx].action!,
                                policy: newCreate[idx].policy!,
                                fields: ['*'],
                                permissions: null,
                                validation: null,
                                presets: null,
                            };
                        }
                        return { ...prev, create: newCreate };
                    });
                } else {
                    // Update existing permission to full access
                    setPermissionChanges(prev => {
                        // Remove from delete list if it was there
                        const newDelete = prev.delete.filter(d => d !== idStr);
                        // Remove from update if already there
                        const filtered = prev.update.filter(p => String(p.id) !== idStr);
                        // Add updated version
                        return {
                            ...prev,
                            delete: newDelete,
                            update: [...filtered, {
                                id: idStr,
                                collection,
                                action,
                                policy: id,
                                fields: ['*'],
                                permissions: null,
                                validation: null,
                                presets: null,
                            }],
                        };
                    });
                }
            } else {
                // Check if this permission was previously deleted (need to "undelete" it)
                // Find if it exists in fetchedPermissions but was staged for deletion
                const deletedPerm = fetchedPermissions?.find(
                    p => p.collection === collection && p.action === action
                );

                if (deletedPerm && permissionChanges.delete.includes(String(deletedPerm.id))) {
                    // Remove from delete list and add to update list to restore with full access
                    setPermissionChanges(prev => ({
                        ...prev,
                        delete: prev.delete.filter(d => d !== String(deletedPerm.id)),
                        update: [...prev.update.filter(p => String(p.id) !== String(deletedPerm.id)), {
                            id: String(deletedPerm.id),
                            collection,
                            action,
                            policy: id,
                            fields: ['*'],
                            permissions: null,
                            validation: null,
                            presets: null,
                        }],
                    }));
                } else {
                    // Create new full access permission
                    setPermissionChanges(prev => ({
                        ...prev,
                        create: [...prev.create, {
                            policy: id,
                            collection,
                            action,
                            fields: ['*'],
                            permissions: null,
                            validation: null,
                            presets: null,
                        }],
                    }));
                }
            }
        }
        // 'custom' is handled via the permission detail sheet
    };

    // Handle opening custom access sheet
    const handleOpenCustomAccess = (
        collection: string,
        action: ActionType,
        existingPermission?: PermissionItem
    ) => {
        if (!id || isNew) return;

        // Find the effective permission data (could be from fetched or staged)
        let permissionData: Partial<Permission> | undefined;

        if (existingPermission) {
            // If it's a temp permission, get from staged creates
            const idStr = String(existingPermission.id);
            if (idStr.startsWith('temp-')) {
                const idx = parseInt(idStr.replace('temp-', ''));
                permissionData = permissionChanges.create[idx];
            } else {
                // Check if there's a staged update for this permission
                const stagedUpdate = permissionChanges.update.find(u => String(u.id) === idStr);
                permissionData = stagedUpdate || existingPermission;
            }
        }

        setActivePermission({
            permissionId: existingPermission ? (String(existingPermission.id).startsWith('temp-') ? null : String(existingPermission.id)) : null,
            collection,
            action,
            permissionData,
        });
    };

    // Handle saving permission from the detail sheet (stages changes locally, NO API call)
    const handleSavePermission = async (updatedPermission: Partial<Permission>): Promise<void> => {
        if (!id || !activePermission) return;

        const { permissionId, collection, action } = activePermission;

        if (permissionId) {
            // Update existing permission (stage the update)
            setPermissionChanges(prev => {
                // Remove any existing update for this permission
                const filteredUpdates = prev.update.filter(p => String(p.id) !== String(permissionId));
                return {
                    ...prev,
                    update: [...filteredUpdates, {
                        id: permissionId,
                        collection,
                        action,
                        policy: id,
                        fields: updatedPermission.fields || ['*'],
                        permissions: updatedPermission.permissions || null,
                        validation: updatedPermission.validation || null,
                        presets: updatedPermission.presets || null,
                    }],
                };
            });
        } else {
            // Check if we're editing a staged create
            const tempId = activePermission.permissionData ?
                permissions.findIndex(p =>
                    p.collection === collection &&
                    p.action === action &&
                    String(p.id).startsWith('temp-')
                ) : -1;

            if (tempId >= 0) {
                // Update staged create
                setPermissionChanges(prev => {
                    const newCreate = [...prev.create];
                    if (newCreate[tempId]) {
                        newCreate[tempId] = {
                            ...newCreate[tempId],
                            collection: newCreate[tempId].collection!,
                            action: newCreate[tempId].action!,
                            policy: newCreate[tempId].policy!,
                            fields: updatedPermission.fields || ['*'],
                            permissions: updatedPermission.permissions || null,
                            validation: updatedPermission.validation || null,
                            presets: updatedPermission.presets || null,
                        };
                    }
                    return { ...prev, create: newCreate };
                });
            } else {
                // Create new permission (stage the create)
                setPermissionChanges(prev => ({
                    ...prev,
                    create: [...prev.create, {
                        policy: id,
                        collection,
                        action,
                        fields: updatedPermission.fields || ['*'],
                        permissions: updatedPermission.permissions || null,
                        validation: updatedPermission.validation || null,
                        presets: updatedPermission.presets || null,
                    }],
                }));
            }
        }

        setActivePermission(null);
    };

    // Get access level for a permission
    const getAccessLevel = (permission?: PermissionItem): AccessLevel => {
        if (!permission) return 'none';

        // Check if it has custom rules
        const hasCustomPermissions = permission.permissions && Object.keys(permission.permissions).length > 0;
        const hasCustomValidation = permission.validation && Object.keys(permission.validation).length > 0;
        const hasCustomFields = permission.fields && !permission.fields.includes('*');
        const hasPresets = permission.presets && Object.keys(permission.presets).length > 0;

        if (hasCustomPermissions || hasCustomValidation || hasCustomFields || hasPresets) {
            return 'custom';
        }
        return 'full';
    };

    const toggleAction = (action: string) => {
        setNewPermission((prev) => ({
            ...prev,
            actions: prev.actions.includes(action)
                ? prev.actions.filter((a) => a !== action)
                : [...prev.actions, action],
        }));
    };

    if (!isNew && policyLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/admin/settings/policies')}
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-foreground">
                        {isNew ? 'Create Policy' : 'Edit Policy'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {isNew
                            ? 'Define a new access policy'
                            : `Editing ${policy?.name || 'policy'}`}
                    </p>
                </div>
                <Button
                    onClick={handleSubmit}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="h-4 w-4" />
                    )}
                    Save
                </Button>
            </div>

            <div className="grid gap-6">
                {/* Policy Form */}
                <div className="">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="rounded-lg border border-border bg-card p-6">
                            <h3 className="mb-4 font-medium text-foreground">
                                Policy Details
                            </h3>

                            {/* Name */}
                            <div className="mb-4">
                                <Label htmlFor="policy-name">
                                    Name <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="policy-name"
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    placeholder="e.g., Content Editor Policy"
                                    className="mt-1"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div className="mb-4">
                                <Label htmlFor="policy-description">Description</Label>
                                <Textarea
                                    id="policy-description"
                                    value={formData.description}
                                    onChange={(e) =>
                                        setFormData({ ...formData, description: e.target.value })
                                    }
                                    rows={3}
                                    placeholder="Describe what this policy allows..."
                                    className="mt-1"
                                />
                            </div>

                            {/* IP Access */}
                            <div>
                                <Label htmlFor="ip-access">IP Allowlist</Label>
                                <Input
                                    id="ip-access"
                                    type="text"
                                    value={formData.ip_access}
                                    onChange={(e) =>
                                        setFormData({ ...formData, ip_access: e.target.value })
                                    }
                                    placeholder="e.g., 192.168.1.0/24, 10.0.0.1"
                                    className="mt-1"
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Comma-separated IP addresses or CIDR ranges. Leave empty to
                                    allow all IPs.
                                </p>
                            </div>
                        </div>

                        {/* Access Settings */}
                        <div className="rounded-lg border border-border bg-card p-6">
                            <h3 className="mb-4 font-medium text-foreground">
                                Access Settings
                            </h3>

                            <div className="space-y-4">
                                {/* Admin Access */}
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        id="admin-access"
                                        checked={formData.admin_access}
                                        onCheckedChange={(checked) =>
                                            setFormData({
                                                ...formData,
                                                admin_access: checked === true,
                                            })
                                        }
                                    />
                                    <Label htmlFor="admin-access" className="cursor-pointer">
                                        <div className="font-medium text-foreground">
                                            Admin Access
                                        </div>
                                        <div className="text-xs text-muted-foreground font-normal">
                                            Full access to all data and settings
                                        </div>
                                    </Label>
                                </div>

                                {/* App Access */}
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        id="app-access"
                                        checked={formData.app_access}
                                        onCheckedChange={(checked) =>
                                            setFormData({
                                                ...formData,
                                                app_access: checked === true,
                                            })
                                        }
                                    />
                                    <Label htmlFor="app-access" className="cursor-pointer">
                                        <div className="font-medium text-foreground">
                                            App Access
                                        </div>
                                        <div className="text-xs text-muted-foreground font-normal">
                                            Can access the admin application
                                        </div>
                                    </Label>
                                </div>

                                {/* Enforce 2FA */}
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        id="enforce-tfa"
                                        checked={formData.enforce_tfa}
                                        onCheckedChange={(checked) =>
                                            setFormData({
                                                ...formData,
                                                enforce_tfa: checked === true,
                                            })
                                        }
                                    />
                                    <Label htmlFor="enforce-tfa" className="cursor-pointer">
                                        <div className="font-medium text-foreground">
                                            Require Two-Factor Authentication
                                        </div>
                                        <div className="text-xs text-muted-foreground font-normal">
                                            Users must have 2FA enabled to use this policy
                                        </div>
                                    </Label>
                                </div>
                            </div>
                        </div>

                        {/* Admin Access Notice */}
                        {!isNew && formData.admin_access && (
                            <div className="rounded-lg border border-primary/20 bg-primary/5 p-6">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                        <CheckCircle className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-foreground">
                                            Full Admin Access
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            This policy grants full access to all data and settings. No additional permissions configuration is needed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Permissions Section - Hidden when admin_access is enabled */}
                        {!isNew && !formData.admin_access && (
                            <div className="rounded-lg border border-border bg-card p-6">
                                <div className="mb-4 flex items-center justify-between">
                                    <h3 className="font-medium text-foreground">
                                        Permissions
                                    </h3>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setShowPermissionModal(true)}
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Collection
                                    </Button>
                                </div>

                                {/* Permissions Table Header */}
                                <div className="overflow-hidden rounded-lg border border-border pb-32">
                                    <table className="w-full">
                                        <thead className="bg-muted">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                                    Collection
                                                </th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                                    <div className="flex items-center gap-2">
                                                        Actions
                                                        {formData.app_access && (
                                                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                                                                Required for App Access
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">

                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {/* App Access Minimal Permissions */}
                                            {formData.app_access && APP_ACCESS_MINIMAL_PERMISSIONS.map((appPerm) => {
                                                // Check if there's a custom permission for this collection
                                                const customPerms: PermissionItem[] = permissionsByCollection?.[appPerm.collection] || [];
                                                return (
                                                    <tr key={`app-${appPerm.collection}`} className="bg-card">
                                                        <td className="px-4 py-3">
                                                            <span className="font-medium text-foreground">
                                                                {appPerm.collection}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex flex-wrap gap-1">
                                                                {ACTIONS.map((action) => {
                                                                    const isAppAction = appPerm.actions.includes(action);
                                                                    const customPerm = customPerms.find((p: PermissionItem) => p.action === action);
                                                                    const isRequired = isAppAction && appPerm.required;

                                                                    // Determine access level
                                                                    let accessLevel: AccessLevel = 'none';
                                                                    if (isAppAction || customPerm) {
                                                                        accessLevel = getAccessLevel(customPerm) === 'custom' ? 'custom' : 'full';
                                                                    }

                                                                    return (
                                                                        <ActionButton
                                                                            key={action}
                                                                            collection={appPerm.collection}
                                                                            action={action}
                                                                            accessLevel={accessLevel}
                                                                            isAppMinimal={isRequired}
                                                                            onChangeAccess={(level) =>
                                                                                handleAccessChange(appPerm.collection, action, level, customPerm)
                                                                            }
                                                                            onCustomAccess={() =>
                                                                                handleOpenCustomAccess(appPerm.collection, action, customPerm)
                                                                            }
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {!appPerm.required && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {/* Custom Permissions (not in app access list) */}
                                            {permissionsByCollection && (Object.entries(permissionsByCollection) as [string, PermissionItem[]][])
                                                .filter(([collection]) =>
                                                    !formData.app_access ||
                                                    !APP_ACCESS_MINIMAL_PERMISSIONS.some(ap => ap.collection === collection)
                                                )
                                                .map(([collection, perms]) => (
                                                    <tr key={collection} className="bg-card">
                                                        <td className="px-4 py-3">
                                                            <span className="font-medium text-foreground">
                                                                {collection}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex flex-wrap gap-1">
                                                                {ACTIONS.map((action) => {
                                                                    const perm = perms.find((p: PermissionItem) => p.action === action);
                                                                    return (
                                                                        <ActionButton
                                                                            key={action}
                                                                            collection={collection}
                                                                            action={action}
                                                                            accessLevel={getAccessLevel(perm)}
                                                                            isAppMinimal={false}
                                                                            onChangeAccess={(level) =>
                                                                                handleAccessChange(collection, action, level, perm)
                                                                            }
                                                                            onCustomAccess={() =>
                                                                                handleOpenCustomAccess(collection, action, perm)
                                                                            }
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-muted-foreground hover:text-red-600"
                                                                onClick={() => {
                                                                    // Delete all permissions for this collection
                                                                    perms.forEach((p: PermissionItem) => handleDeletePermission(p.id));
                                                                }}
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}

                                            {/* Empty state */}
                                            {!formData.app_access && (!permissionsByCollection || Object.keys(permissionsByCollection).length === 0) && (
                                                <tr>
                                                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                                                        No permissions configured. Click "Add Collection" to add permissions.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Reset Permissions Links */}
                                {formData.app_access && (
                                    <div className="mt-4 text-center text-sm text-muted-foreground">
                                        Reset System Permissions to:{' '}
                                        <Button variant="link" type="button" className="text-blue-600 h-auto p-0">
                                            App Access Minimum
                                        </Button>
                                        {' / '}
                                        <Button variant="link" type="button" className="text-blue-600 h-auto p-0">
                                            Recommended Defaults
                                        </Button>
                                    </div>
                                )}
                            </div>

                        )}
                    </form>

                </div>


            </div>

            {/* Add Permission Modal */}
            <Dialog open={showPermissionModal} onOpenChange={setShowPermissionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Permission</DialogTitle>
                        <DialogDescription>
                            Configure permissions for a collection
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Collection Select */}
                        <div>
                            <Label htmlFor="collection-select">Collection</Label>
                            <Select
                                value={newPermission.collection}
                                onValueChange={(value) =>
                                    setNewPermission({
                                        ...newPermission,
                                        collection: value,
                                    })
                                }
                            >
                                <SelectTrigger id="collection-select" className="mt-1">
                                    <SelectValue placeholder="Select a collection..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* User-created collections */}
                                    {availableCollections.userCollections.length > 0 && (
                                        <SelectGroup>
                                            <SelectLabel>Collections</SelectLabel>
                                            {availableCollections.userCollections.map((collection) => (
                                                <SelectItem key={collection.collection} value={collection.collection}>
                                                    {collection.collection}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    )}

                                    {/* Separator between user and system collections */}
                                    {availableCollections.userCollections.length > 0 &&
                                        availableCollections.systemCollections.length > 0 && (
                                            <SelectSeparator />
                                        )}

                                    {/* System collections */}
                                    {availableCollections.systemCollections.length > 0 && (
                                        <SelectGroup>
                                            <SelectLabel>System Collections</SelectLabel>
                                            {availableCollections.systemCollections.map((collection) => (
                                                <SelectItem key={collection} value={collection}>
                                                    {collection}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Actions */}
                        <div>
                            <Label>Actions</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {ACTIONS.map((action) => (
                                    <Button
                                        key={action}
                                        type="button"
                                        variant={newPermission.actions.includes(action) ? 'default' : 'secondary'}
                                        size="sm"
                                        onClick={() => toggleAction(action)}
                                    >
                                        {newPermission.actions.includes(action) && (
                                            <Check className="h-3 w-3" />
                                        )}
                                        {action}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowPermissionModal(false);
                                setNewPermission({ collection: '', actions: [] });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddPermissions}
                            disabled={
                                !newPermission.collection ||
                                newPermission.actions.length === 0
                            }
                        >
                            Add
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Permission Detail Sheet */}
            {activePermission && (
                <PermissionDetailSheet
                    isOpen={!!activePermission}
                    onClose={() => setActivePermission(null)}
                    permissionId={activePermission.permissionId}
                    permissionData={{
                        collection: activePermission.collection,
                        action: activePermission.action,
                    }}
                    policyId={id!}
                    policyName={formData.name}
                    appMinimalFields={
                        formData.app_access &&
                            APP_ACCESS_MINIMAL_PERMISSIONS.some(
                                (ap) =>
                                    ap.collection === activePermission.collection &&
                                    ap.actions.includes(activePermission.action)
                            )
                            ? ['*']  // App minimal permissions always have all fields
                            : null
                    }
                    onSave={handleSavePermission}
                />
            )}
        </div>
    );
}
